import { useState } from 'react';
import type { CSSProperties } from 'react';
import type { CropRect } from '@lumos/core';
import { useSelectedPhoto, useStore } from '../../state/store.ts';
import { GroupLabel, Panel } from '../controls/Panel.tsx';
import { Slider } from '../controls/Slider.tsx';
import { Tip } from '../controls/Tooltip.tsx';

const ROW: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 5,
  margin: '4px 0 2px',
};

const GRID: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '6px 12px',
  margin: '6px 0 2px',
};

const FULL_FRAME: CropRect = { x: 0, y: 0, width: 1, height: 1 };

type CropField = 'x' | 'y' | 'width' | 'height';

const CROP_FIELDS: { key: CropField; label: string }[] = [
  { key: 'x', label: 'Left' },
  { key: 'y', label: 'Top' },
  { key: 'width', label: 'Width' },
  { key: 'height', label: 'Height' },
];

/** `ratio` is width / height in the frame's own orientation; null means no crop. */
const ASPECTS: { id: string; label: string; ratio: number | null }[] = [
  { id: 'original', label: 'Original', ratio: null },
  { id: 'square', label: '1:1', ratio: 1 },
  { id: 'four-five', label: '4:5', ratio: 4 / 5 },
  { id: 'three-two', label: '3:2', ratio: 3 / 2 },
  { id: 'sixteen-nine', label: '16:9', ratio: 16 / 9 },
];

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

function round(value: number): number {
  return Math.round(value * 10000) / 10000;
}

/** Keeps the box inside the frame and large enough to render. */
function normaliseCrop(rect: CropRect): CropRect {
  const width = clamp(rect.width, 0.01, 1);
  const height = clamp(rect.height, 0.01, 1);
  return {
    width: round(width),
    height: round(height),
    x: round(clamp(rect.x, 0, 1 - width)),
    y: round(clamp(rect.y, 0, 1 - height)),
  };
}

function withField(rect: CropRect, field: CropField, value: number): CropRect {
  return {
    x: field === 'x' ? value : rect.x,
    y: field === 'y' ? value : rect.y,
    width: field === 'width' ? value : rect.width,
    height: field === 'height' ? value : rect.height,
  };
}

/** Largest centred box of `ratio` that fits a frame of `frameAspect`. */
function centredCrop(ratio: number, frameAspect: number): CropRect {
  if (ratio > frameAspect) {
    const height = round(frameAspect / ratio);
    return { x: 0, y: round((1 - height) / 2), width: 1, height };
  }
  const width = round(ratio / frameAspect);
  return { x: round((1 - width) / 2), y: 0, width, height: 1 };
}

export function GeometryPanel() {
  const photo = useSelectedPhoto();
  const updateRecipe = useStore((state) => state.updateRecipe);
  const commit = useStore((state) => state.commit);
  const [picked, setPicked] = useState<{ photoId: string; id: string } | null>(null);
  const [editing, setEditing] = useState<Partial<Record<CropField, string>>>({});

  if (!photo) return null;
  const recipe = photo.recipe;
  const image = photo.image;

  const active =
    recipe.crop !== null ||
    recipe.rotate !== 0 ||
    recipe.orientation !== 0 ||
    recipe.flipHorizontal ||
    recipe.flipVertical;

  // The crop box is normalised against the *oriented* source, so an odd number
  // of quarter turns swaps the frame's width and height.
  const turned = recipe.orientation % 2 === 1;
  const frameWidth = image ? (turned ? image.fullHeight : image.fullWidth) : 0;
  const frameHeight = image ? (turned ? image.fullWidth : image.fullHeight) : 0;
  const frameAspect = frameWidth > 0 && frameHeight > 0 ? frameWidth / frameHeight : 0;
  const portrait = frameAspect > 0 && frameAspect < 1;

  // Presets follow the frame: on a vertical photo "3:2" means 2:3.
  const orientRatio = (ratio: number) => (portrait && ratio !== 1 ? 1 / ratio : ratio);

  const crop = recipe.crop ?? FULL_FRAME;

  const derivedAspect = (() => {
    if (!recipe.crop) return 'original';
    if (frameAspect <= 0) return 'free';
    const cropAspect = (recipe.crop.width / recipe.crop.height) * frameAspect;
    const match = ASPECTS.find(
      (preset) => preset.ratio !== null && Math.abs(cropAspect / orientRatio(preset.ratio) - 1) < 0.005,
    );
    return match ? match.id : 'free';
  })();
  const aspect = picked && picked.photoId === photo.id ? picked.id : derivedAspect;

  const applyAspect = (preset: { id: string; ratio: number | null }) => {
    setPicked({ photoId: photo.id, id: preset.id });
    setEditing({});
    updateRecipe(
      (draft) => {
        draft.crop = preset.ratio === null ? null : centredCrop(orientRatio(preset.ratio), frameAspect);
      },
      { commit: true },
    );
  };

  const applyFree = () => {
    setPicked({ photoId: photo.id, id: 'free' });
    if (recipe.crop) return;
    updateRecipe(
      (draft) => {
        draft.crop = { ...FULL_FRAME };
      },
      { commit: true },
    );
  };

  const changeField = (field: CropField, text: string) => {
    setEditing((previous) => ({ ...previous, [field]: text }));
    const parsed = Number.parseFloat(text);
    if (!Number.isFinite(parsed)) return;
    setPicked({ photoId: photo.id, id: 'free' });
    updateRecipe((draft) => {
      draft.crop = normaliseCrop(withField(crop, field, parsed));
    });
  };

  const blurField = (field: CropField) => {
    setEditing((previous) => {
      const next = { ...previous };
      delete next[field];
      return next;
    });
    commit();
  };

  const turn = (quarterTurns: number) =>
    updateRecipe(
      (draft) => {
        draft.orientation = (draft.orientation + quarterTurns + 4) % 4;
      },
      { commit: true },
    );

  return (
    <Panel id="geometry" title="Geometry" active={active}>
      <Slider
        label="Straighten"
        value={recipe.rotate}
        min={-45}
        max={45}
        step={0.1}
        neutral={0}
        unit="°"
        help="Levels a horizon or a vertical. The crop pulls in to stay inside the image."
        onChange={(value) =>
          updateRecipe((draft) => {
            draft.rotate = value;
          })
        }
        onCommit={commit}
      />

      <GroupLabel>Orientation</GroupLabel>
      <div style={ROW}>
        <button type="button" className="btn tiny" onClick={() => turn(-1)}>
          Rotate left
        </button>
        <button type="button" className="btn tiny" onClick={() => turn(1)}>
          Rotate right
        </button>
        <span className="label">{recipe.orientation * 90}°</span>
      </div>
      <div style={ROW}>
        <button
          type="button"
          className={`btn tiny${recipe.flipHorizontal ? ' active' : ''}`}
          aria-pressed={recipe.flipHorizontal}
          onClick={() =>
            updateRecipe(
              (draft) => {
                draft.flipHorizontal = !draft.flipHorizontal;
              },
              { commit: true },
            )
          }
        >
          {recipe.flipHorizontal ? '✓ ' : ''}Flip horizontal
        </button>
        <button
          type="button"
          className={`btn tiny${recipe.flipVertical ? ' active' : ''}`}
          aria-pressed={recipe.flipVertical}
          onClick={() =>
            updateRecipe(
              (draft) => {
                draft.flipVertical = !draft.flipVertical;
              },
              { commit: true },
            )
          }
        >
          {recipe.flipVertical ? '✓ ' : ''}Flip vertical
        </button>
      </div>

      <GroupLabel>
        <Tip
          title="Crop"
          body="Presets follow the frame, so 3:2 becomes 2:3 on a vertical photo."
        >
          Crop
        </Tip>
      </GroupLabel>
      <div style={ROW} role="group" aria-label="Crop aspect ratio">
        {ASPECTS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className={`btn tiny${aspect === preset.id ? ' active' : ''}`}
            aria-pressed={aspect === preset.id}
            disabled={preset.ratio !== null && frameAspect <= 0}
            onClick={() => applyAspect(preset)}
          >
            {preset.label}
          </button>
        ))}
        <button
          type="button"
          className={`btn tiny${aspect === 'free' ? ' active' : ''}`}
          aria-pressed={aspect === 'free'}
          onClick={applyFree}
        >
          Free
        </button>
      </div>

      <GroupLabel>
        <Tip
          title="Crop box"
          body="Fractions of the frame, measured from the top left after any rotation."
        >
          Box
        </Tip>
      </GroupLabel>
      <div style={GRID}>
        {CROP_FIELDS.map((field) => (
          <label className="field" key={field.key}>
            <span className="label">{field.label}</span>
            <input
              type="number"
              min={0}
              max={1}
              step={0.001}
              value={editing[field.key] ?? crop[field.key].toFixed(3)}
              onChange={(event) => changeField(field.key, event.target.value)}
              onBlur={() => blurField(field.key)}
            />
          </label>
        ))}
      </div>

      <div style={ROW}>
        <button
          type="button"
          className="btn tiny"
          disabled={recipe.crop === null}
          onClick={() => {
            setPicked({ photoId: photo.id, id: 'original' });
            setEditing({});
            updateRecipe(
              (draft) => {
                draft.crop = null;
              },
              { commit: true },
            );
          }}
        >
          Reset crop
        </button>
        {frameWidth > 0 ? (
          <span className="label">
            {Math.round(crop.width * frameWidth)} × {Math.round(crop.height * frameHeight)} px
          </span>
        ) : null}
      </div>
    </Panel>
  );
}
